import {IssueRelativeTime} from '@github-ui/ago'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {ProfileReference} from '@github-ui/profile-reference'
import {useAnalytics} from '@github-ui/use-analytics'
import {type ColorName, isColorName, useNamedColor} from '@github-ui/use-named-color'
import {CheckIcon, IssueClosedIcon, IssueOpenedIcon, SkipIcon} from '@primer/octicons-react'
import {AnchoredOverlay, Button, Label, Link} from '@primer/react'
import {clsx} from 'clsx'
import type {ReactNode} from 'react'
import {memo, useCallback, useRef} from 'react'

import styles from './RecentAgentActivity.module.css'
import {useHoverPopover} from './use-hover-popover'
import type {
  CloseStateReason,
  DuplicateTarget,
  PendingSuggestion,
  SuggestionConfidence,
} from './use-recent-agent-activity'
import {useRejectAnimation} from './use-reject-animation'

function resolveDotColor(color: string | null | undefined): string | undefined {
  if (!color) return undefined
  // Hex string with or without leading '#'
  if (/^#?[0-9a-f]{3,8}$/i.test(color)) {
    return color.startsWith('#') ? color : `#${color}`
  }
  return undefined
}

type LabelColorDotProps = {
  color?: string | null
}

function LabelColorDot({color}: LabelColorDotProps) {
  const isNamed = Boolean(color && isColorName(color))
  const {accent} = useNamedColor(isNamed ? (color as ColorName) : undefined)
  const resolved = isNamed ? accent : resolveDotColor(color)
  if (!resolved) return null
  return <span className={styles.chipDot} style={{backgroundColor: resolved}} aria-hidden="true" />
}

type ChipValueProps = {
  name: string
  color?: string | null
  // When provided, renders the suggested assignee's avatar instead of a color dot.
  avatarUrl?: string | null
}

function ChipValue({name, color, avatarUrl}: ChipValueProps) {
  return (
    <span className={styles.chipValueInner}>
      {avatarUrl ? (
        <GitHubAvatar src={avatarUrl} size={16} alt="" className={styles.chipAvatar} />
      ) : (
        <LabelColorDot color={color} />
      )}
      <span className={styles.chipValueText}>{name}</span>
    </span>
  )
}

type CloseChipValueProps = {
  stateReason: CloseStateReason
  duplicateOf: DuplicateTarget | null
}

function CloseChipValue({stateReason, duplicateOf}: CloseChipValueProps) {
  if (stateReason === 'COMPLETED') {
    return (
      <span className={styles.chipValueInner}>
        <IssueClosedIcon size={12} className={styles.chipCloseIconDone} aria-hidden="true" />
        <span className={styles.chipValueText}>completed</span>
      </span>
    )
  }
  if (stateReason === 'NOT_PLANNED') {
    return (
      <span className={styles.chipValueInner}>
        <SkipIcon size={12} className={styles.chipCloseIconMuted} aria-hidden="true" />
        <span className={styles.chipValueText}>not planned</span>
      </span>
    )
  }
  // DUPLICATE — only reachable when `duplicateOf` is present (processor drops it otherwise).
  if (!duplicateOf) return null
  return (
    <span className={styles.chipValueInner}>
      <span className={styles.chipValueText}>duplicate of</span>
      <IssueOpenedIcon size={12} className={styles.chipCloseIconOpen} aria-hidden="true" />
      <span className={styles.chipValueText}>#{duplicateOf.number}</span>
    </span>
  )
}

function renderChipValue(suggestion: PendingSuggestion): ReactNode {
  if (suggestion.type === 'issue_closed' && suggestion.closeStateReason) {
    return <CloseChipValue stateReason={suggestion.closeStateReason} duplicateOf={suggestion.duplicateOf} />
  }
  return <ChipValue name={suggestion.value} color={suggestion.color} avatarUrl={suggestion.avatarUrl} />
}

// Builds the rationale popover body for a close suggestion. Duplicate suggestions
// get a link to the target issue/PR above the free-form rationale so reviewers can
// open it in a new tab without leaving the panel.
function renderCloseRationale(suggestion: PendingSuggestion): ReactNode {
  const {duplicateOf, closeStateReason, rationale} = suggestion
  if (closeStateReason === 'DUPLICATE' && duplicateOf) {
    return (
      <>
        <div className={styles.duplicateTarget}>
          <span>Duplicate of </span>
          <Link href={duplicateOf.url} inline>
            {duplicateOf.repoNameWithOwner}#{duplicateOf.number}
          </Link>
          <span className={styles.duplicateTargetTitle}>{duplicateOf.title}</span>
        </div>
        {rationale ? <div>{rationale}</div> : null}
      </>
    )
  }
  return rationale ?? null
}

function renderRationaleNode(suggestion: PendingSuggestion): {node: ReactNode; length: number} {
  if (suggestion.type === 'issue_closed') {
    const node = renderCloseRationale(suggestion)
    const length =
      (suggestion.rationale?.length ?? 0) +
      (suggestion.closeStateReason === 'DUPLICATE' && suggestion.duplicateOf ? suggestion.duplicateOf.title.length : 0)
    return {node, length}
  }
  return {node: suggestion.rationale ?? null, length: suggestion.rationale?.length ?? 0}
}

function closeChipAriaValue(suggestion: PendingSuggestion): string {
  if (suggestion.closeStateReason === 'COMPLETED') return 'completed'
  if (suggestion.closeStateReason === 'NOT_PLANNED') return 'not planned'
  if (suggestion.closeStateReason === 'DUPLICATE' && suggestion.duplicateOf) {
    return `duplicate of ${suggestion.duplicateOf.repoNameWithOwner}#${suggestion.duplicateOf.number}`
  }
  return ''
}

type ChipProps = {
  fieldName: string
  children: ReactNode
  confidence: SuggestionConfidence | null
  ariaLabel: string
  rationale: ReactNode
  // Total rationale text length, used for analytics.
  rationaleLength: number
  author: SuggestionAuthor | null
  createdAt: string | null
}

type SuggestionAuthor = {
  displayName: string | null
  login: string
  avatarUrl: string
  profileUrl: string
  isAgent: boolean
}

function suggestionAuthor(suggestion: PendingSuggestion): SuggestionAuthor {
  return {
    displayName: suggestion.actorDisplayName ?? suggestion.actorName,
    login: suggestion.actorLogin,
    avatarUrl: suggestion.actorAvatarUrl,
    profileUrl: suggestion.actorProfileUrl,
    isAgent: suggestion.actorTypename === 'Bot',
  }
}

function stripBotSuffix(name: string): string {
  return name.replace(/\[bot\]$/i, '')
}

function SuggestionAuthorHeader({author, createdAt}: {author: SuggestionAuthor | null; createdAt: string | null}) {
  if (!author) return null

  const {displayName, login, avatarUrl, profileUrl, isAgent} = author
  const reference = (
    <ProfileReference
      login={isAgent ? stripBotSuffix(displayName ?? login) : login}
      profileName={displayName}
      isAgent={isAgent}
    />
  )

  return (
    <div className={styles.authorHeader}>
      <GitHubAvatar src={avatarUrl} size={20} alt="" square={isAgent} className={styles.authorAvatar} />
      <Link href={profileUrl} className={clsx(styles.authorName, 'color-fg-default')}>
        <span className="sr-only">Suggested by </span>
        {reference}
      </Link>
      {createdAt ? (
        <IssueRelativeTime
          datetime={createdAt}
          className={styles.authorTimestamp}
          data-testid="suggestion-author-timestamp"
        />
      ) : null}
    </div>
  )
}

type SuggestionChipProps = ChipProps & {
  applied?: boolean
  onApprove?: () => void
  onReject?: () => void
  wrapperClassName?: string
  onWrapperTransitionEnd?: (event: React.TransitionEvent<HTMLDivElement>) => void
}

const CONFIDENCE_LABELS: Record<SuggestionConfidence, string> = {
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
}

const CONFIDENCE_LABEL_VARIANTS: Record<SuggestionConfidence, 'success' | 'accent' | 'done'> = {
  HIGH: 'success',
  MEDIUM: 'accent',
  LOW: 'done',
}

// Presentational chip shared by the single-suggestion and grouped-label chips.
// Renders the field/value, the hover rationale popover, and the accept/decline
// actions. Callers build the props via the chip-props builders below.
function SuggestionChip({
  fieldName,
  children,
  confidence,
  ariaLabel,
  rationale,
  rationaleLength,
  author,
  createdAt,
  applied = false,
  onApprove,
  onReject,
  wrapperClassName,
  onWrapperTransitionEnd,
}: SuggestionChipProps) {
  const anchorRef = useRef<HTMLButtonElement>(null)
  const {sendAnalyticsEvent} = useAnalytics()

  const interactive = Boolean(onApprove || onReject || rationale)

  const handleFirstOpen = useCallback(() => {
    if (rationaleLength > 0) {
      sendAnalyticsEvent('recent_agent_actions_panel.rationale_clicked', 'RECENT_AGENT_ACTIVITY', {
        rationaleLength,
      })
    }
  }, [rationaleLength, sendAnalyticsEvent])

  const {open, anchorRegionProps, anchorProps, contentProps, closeNow} = useHoverPopover({
    onFirstOpen: handleFirstOpen,
  })

  const handleApproveClick = useCallback(() => {
    closeNow()
    onApprove?.()
  }, [closeNow, onApprove])

  const handleRejectClick = useCallback(() => {
    closeNow()
    onReject?.()
  }, [closeNow, onReject])

  const chipContent = (
    <>
      <span className={styles.chipFieldName}>{fieldName}</span>
      <span className={styles.chipArrow} aria-hidden="true">
        →
      </span>
      <span className={styles.chipValue}>{children}</span>
      {applied && <CheckIcon size={12} className={styles.chipCheck} aria-hidden="true" />}
    </>
  )

  if (!interactive) {
    return (
      <div
        className={clsx(styles.chip, applied && styles.chipApplied)}
        data-confidence={confidence ?? undefined}
        role="listitem"
      >
        {chipContent}
      </div>
    )
  }

  return (
    <div
      {...anchorRegionProps}
      role="listitem"
      className={clsx(styles.chipWrapper, wrapperClassName)}
      onTransitionEnd={onWrapperTransitionEnd}
    >
      <button
        {...anchorProps}
        ref={anchorRef}
        type="button"
        className={clsx(styles.chip, styles.chipButton, applied && styles.chipApplied)}
        data-confidence={confidence ?? undefined}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {chipContent}
      </button>
      {open && (
        <AnchoredOverlay
          open={open}
          anchorRef={anchorRef}
          renderAnchor={null}
          onClose={closeNow}
          // Don't trap focus on open: the overlay is hover/focus-triggered, so
          // pulling focus into it would be disruptive. Keyboard users can still
          // Tab into the rationale actions from the chip button.
          focusTrapSettings={{disabled: true}}
        >
          <div {...contentProps} className={styles.rationaleOverlay}>
            <SuggestionAuthorHeader author={author} createdAt={createdAt} />
            {confidence && (
              <Label variant={CONFIDENCE_LABEL_VARIANTS[confidence]} className={styles.confidenceLabel}>
                {`${CONFIDENCE_LABELS[confidence]} confidence`}
              </Label>
            )}
            {rationale ? <div className={styles.rationaleBody}>{rationale}</div> : null}
            {(onApprove || onReject) && (
              <div className={styles.rationaleActions}>
                {onReject && (
                  <Button variant="default" size="small" onClick={handleRejectClick}>
                    Decline
                  </Button>
                )}
                {onApprove && (
                  <Button variant="primary" size="small" onClick={handleApproveClick}>
                    Accept
                  </Button>
                )}
              </div>
            )}
          </div>
        </AnchoredOverlay>
      )}
    </div>
  )
}

// Builds the accessible name for a single-suggestion chip, for both pending and
// applied states.
function buildSuggestionAriaLabel(suggestion: PendingSuggestion, applied: boolean, hasRationale: boolean): string {
  const ariaValue = suggestion.type === 'issue_closed' ? closeChipAriaValue(suggestion) : suggestion.value
  if (applied) {
    return `Applied ${suggestion.displayFieldName.toLowerCase()}: ${ariaValue}${hasRationale ? '. View rationale' : ''}`
  }
  const valuePart = ariaValue ? `: ${ariaValue}` : ''
  const confidencePart = suggestion.confidence ? `. ${CONFIDENCE_LABELS[suggestion.confidence]} confidence` : ''
  return `Pending ${suggestion.displayFieldName} suggestion${valuePart}${confidencePart}. View details`
}

// Builds the presentational props for a single suggestion chip (applied or pending).
function suggestionChipProps(suggestion: PendingSuggestion, applied: boolean): ChipProps {
  const {node: rationale, length: rationaleLength} = renderRationaleNode(suggestion)

  return {
    fieldName: suggestion.displayFieldName,
    children: renderChipValue(suggestion),
    confidence: suggestion.confidence,
    ariaLabel: buildSuggestionAriaLabel(suggestion, applied, Boolean(rationale)),
    rationale,
    rationaleLength,
    author: suggestionAuthor(suggestion),
    createdAt: suggestion.createdAt,
  }
}

// Builds the presentational props for a grouped-label chip. The value renders the
// labels comma-separated and the rationale becomes a per-label list.
function labelGroupChipProps(suggestions: PendingSuggestion[]): ChipProps {
  const confidence = suggestions[0]?.confidence ?? null
  const labelNames = suggestions.map(s => s.value).join(', ')
  const rationaleLength = suggestions.reduce((sum, s) => sum + (s.rationale?.length ?? 0), 0)
  const hasAnyRationale = suggestions.some(s => Boolean(s.rationale))
  const rationale = hasAnyRationale ? (
    <ul className={styles.rationaleList}>
      {suggestions.map(s => (
        <li key={s.labelId ?? s.value}>
          <span className={styles.rationaleListLabel}>
            <LabelColorDot color={s.color} />
            {s.value}
          </span>
          {s.rationale ? <>: {s.rationale}</> : null}
        </li>
      ))}
    </ul>
  ) : null

  return {
    fieldName: 'Labels',
    children: suggestions.map((s, index) => (
      <span key={s.labelId ?? `${s.value}-${index}`} className={styles.chipValueGroup}>
        {index > 0 && (
          <span className={styles.chipValueSeparator} aria-hidden="true">
            ,
          </span>
        )}
        <ChipValue name={s.value} color={s.color} />
      </span>
    )),
    confidence,
    ariaLabel: `Pending label suggestions: ${labelNames}${
      confidence ? `. ${CONFIDENCE_LABELS[confidence]} confidence` : ''
    }. View details`,
    rationale,
    rationaleLength,
    author: suggestions[0] ? suggestionAuthor(suggestions[0]) : null,
    createdAt: suggestions[0]?.createdAt ?? null,
  }
}

type AppliedSuggestionChipProps = {
  suggestion: PendingSuggestion
}

// Renders a recently-approved suggestion as a non-interactive chip with a
// checkmark. This is a transient, optimistic-only state: the panel queries
// only `pendingSuggestions`, so on refresh the applied row disappears.
// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name
export const AppliedSuggestionChip = memo(function AppliedSuggestionChip({suggestion}: AppliedSuggestionChipProps) {
  return <SuggestionChip {...suggestionChipProps(suggestion, true)} applied />
})

type PendingSuggestionChipProps = {
  suggestion: PendingSuggestion
  onApprove: (suggestion: PendingSuggestion) => void
  onReject: (suggestion: PendingSuggestion) => void
}

// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name
export const PendingSuggestionChip = memo(function PendingSuggestionChip({
  suggestion,
  onApprove,
  onReject,
}: PendingSuggestionChipProps) {
  const reject = useRejectAnimation(useCallback(() => onReject(suggestion), [onReject, suggestion]))
  const handleApprove = useCallback(() => onApprove(suggestion), [onApprove, suggestion])

  return (
    <SuggestionChip
      {...suggestionChipProps(suggestion, false)}
      onApprove={handleApprove}
      onReject={reject.startReject}
      wrapperClassName={reject.wrapperClassName}
      onWrapperTransitionEnd={reject.onWrapperTransitionEnd}
    />
  )
})

type PendingLabelGroupChipProps = {
  suggestions: PendingSuggestion[]
  onApprove: (suggestions: PendingSuggestion[]) => void
  onReject: (suggestions: PendingSuggestion[]) => void
}

// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name
export const PendingLabelGroupChip = memo(function PendingLabelGroupChip({
  suggestions,
  onApprove,
  onReject,
}: PendingLabelGroupChipProps) {
  const reject = useRejectAnimation(useCallback(() => onReject(suggestions), [onReject, suggestions]))
  const handleApprove = useCallback(() => onApprove(suggestions), [onApprove, suggestions])

  return (
    <SuggestionChip
      {...labelGroupChipProps(suggestions)}
      onApprove={handleApprove}
      onReject={reject.startReject}
      wrapperClassName={reject.wrapperClassName}
      onWrapperTransitionEnd={reject.onWrapperTransitionEnd}
    />
  )
})

export type PendingChipItem =
  | {kind: 'single'; suggestion: PendingSuggestion}
  | {kind: 'label-group'; key: string; suggestions: PendingSuggestion[]}

// Group consecutive non-grouping suggestions individually, but bucket label
// suggestions that share the same actor and confidence into a single chip so
// the user can review and approve/reject them together.
export function buildPendingChipItems(suggestions: PendingSuggestion[]): PendingChipItem[] {
  const buckets = new Map<string, PendingSuggestion[]>()
  type Slot = {kind: 'single'; suggestion: PendingSuggestion} | {kind: 'bucket'; key: string}
  const slots: Slot[] = []

  for (const suggestion of suggestions) {
    if (suggestion.type === 'label_added' && suggestion.confidence) {
      const key = `${suggestion.actorId}|${suggestion.confidence}`
      let bucket = buckets.get(key)
      if (!bucket) {
        bucket = []
        buckets.set(key, bucket)
        slots.push({kind: 'bucket', key})
      }
      bucket.push(suggestion)
    } else {
      slots.push({kind: 'single', suggestion})
    }
  }

  return slots.map(slot => {
    if (slot.kind === 'single') return slot
    const bucket = buckets.get(slot.key) ?? []
    if (bucket.length === 1 && bucket[0]) {
      return {kind: 'single', suggestion: bucket[0]}
    }
    return {kind: 'label-group', key: slot.key, suggestions: bucket}
  })
}
